import { motion } from 'framer-motion'
import { Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useMemo, useState, useEffect } from 'react'
import { reportsApi } from '@/services/api'
import { buildCategoryRows, type CategorySpendingRow } from './categoryUtils'

interface SpendingByCategoryProps {
  period?: string
}

export function SpendingByCategory({ period = 'month' }: SpendingByCategoryProps) {
  const { t } = useTranslation()
  const [categories, setCategories] = useState<CategorySpendingRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const hasSpending = useMemo(
    () => categories.some((category) => category.amount > 0),
    [categories]
  )

  useEffect(() => {
    loadSpendingData()
  }, [period])

  const loadSpendingData = async () => {
    setIsLoading(true)
    try {
      // Fetch spending data by category from API
      const response = await reportsApi.getSpendingByCategory({
        period
      })
      
      // Extract data from API response
      const spendingData = response.data.data || response.data
      const categoryData = Array.isArray(spendingData) ? spendingData : []

      const categoryRows = buildCategoryRows(
        categoryData,
        {
          business: t('reports.categories.business'),
          transport: t('reports.categories.transport'),
          restaurants: t('reports.categories.restaurants'),
          retail: t('reports.categories.retail'),
          services: t('reports.categories.services'),
          tourism: t('reports.categories.tourism'),
        },
        {
          business: 'bg-primary',
          transport: 'bg-secondary',
          restaurants: 'bg-accent',
          retail: 'bg-success',
          services: 'bg-warning',
          tourism: 'bg-info',
        }
      )

      setCategories(categoryRows)
    } catch (error) {
      console.error('Error loading spending data:', error)
      setCategories([])
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-text-primary">
            {t('reports.spendingByCategory')}
          </h2>
        </div>
        <div className="text-center py-8">
          <p className="text-text-secondary">{t('common.loading')}</p>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">
          {t('reports.spendingByCategory')}
        </h2>
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Calendar className="h-4 w-4" />
          <span>{t('reports.lastMonth')}</span>
        </div>
      </div>

      {!hasSpending && (
        <div className="mb-4 rounded-lg bg-gray-50 px-4 py-3 text-sm text-text-secondary">
          {t('reports.noSpendingData')}
        </div>
      )}

      <div className="space-y-4">
        {categories.map((category, index) => (
          <motion.div
            key={category.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 + index * 0.1 }}
            className="flex items-center space-x-4"
          >
            <div className="w-4 h-4 rounded-full bg-gray-200 flex-shrink-0">
              <div 
                className={`w-full h-full rounded-full ${category.color}`}
                style={{ width: `${category.percentage}%` }}
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="font-medium text-text-primary">{category.name}</span>
                <span className="text-sm text-text-secondary">
                  {category.amount.toFixed(2)} STN
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                <div 
                  className={`h-2 rounded-full ${category.color}`}
                  style={{ width: `${category.percentage}%` }}
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  )
}

